// pages/DashboardPage.js

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { collection, query, where, onSnapshot, doc, deleteDoc, updateDoc, getDocs } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import './DashboardPage.css';
import ManageCategoriesPage from './ManageCategoriesPage';


// --- Edit User Modal Component ---
function EditUserModal({ user, isOpen, onClose, onSave, isSaving }) {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    role: 'contributor',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        role: user.role || 'contributor',
      });
    }
  }, [user]);

  if (!isOpen || !user) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = (e) => {
    e.preventDefault();
    onSave({
      uid: user.id,
      ...formData
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content edit-user-modal">
        <h2>Edit User</h2>
        <p className="modal-subheader-text">Editing user: <strong>{user.email}</strong></p>
        <form onSubmit={handleSave} className="modal-form">
          <div className="form-field">
            <label htmlFor="editFirstName">First Name</label>
            <input id="editFirstName" name="firstName" type="text" value={formData.firstName} onChange={handleChange} required />
          </div>
          <div className="form-field">
            <label htmlFor="editLastName">Last Name</label>
            <input id="editLastName" name="lastName" type="text" value={formData.lastName} onChange={handleChange} required />
          </div>
          <div className="form-field">
            <label htmlFor="editRole">Role</label>
            <select id="editRole" name="role" value={formData.role} onChange={handleChange}>
              <option value="contributor">Contributor</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-cancel" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className="btn btn-save" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


// --- User Management Component ---
function UserManagementPanel() {
  // State for the new user form
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('contributor');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // State for the user list and actions
  const [usersList, setUsersList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [deletingUserId, setDeletingUserId] = useState(null);

  // State for the edit modal
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isSavingUser, setIsSavingUser] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      setListLoading(true);
      try {
        const usersCollectionRef = collection(db, 'contributors');
        const userSnapshot = await getDocs(usersCollectionRef);
        const allUsers = userSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setUsersList(allUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
        setError("Could not load the list of users.");
      } finally {
        setListLoading(false);
      }
    };
    fetchUsers();
  }, [successMessage]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!firstName || !lastName || !email || !password) { setError("All fields are required."); return; }
    setIsSubmitting(true); setError(''); setSuccessMessage('');
    try {
      const createNewUser = httpsCallable(functions, 'createNewUser');
      const result = await createNewUser({ email, password, firstName, lastName, role });
      if (result.data.success) {
        setSuccessMessage(`Successfully created user: ${firstName} ${lastName}`);
        setFirstName(''); setLastName(''); setEmail(''); setPassword(''); setRole('contributor');
      } else {
        throw new Error(result.data.error || 'An unknown error occurred.');
      }
    } catch (err) {
      console.error("Error creating user:", err);
      setError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditUser = (userId) => {
    const userToEdit = usersList.find(user => user.id === userId);
    if (userToEdit) {
      setEditingUser(userToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleSaveUser = async (updatedUserData) => {
    setIsSavingUser(true);
    try {
      const updateUserFunction = httpsCallable(functions, 'updateUser');
      await updateUserFunction(updatedUserData);

      setUsersList(currentUsers =>
        currentUsers.map(user =>
          user.id === updatedUserData.uid ? { ...user, ...updatedUserData } : user
        )
      );
      
      setIsEditModalOpen(false);

    } catch (err) {
      console.error("Error updating user:", err);
      alert(`Failed to update user: ${err.message}`);
    } finally {
      setIsSavingUser(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (window.confirm("Are you sure you want to permanently delete this user? This action cannot be undone.")) {
      setDeletingUserId(userId);
      try {
        const deleteUserFunction = httpsCallable(functions, 'deleteUser');
        await deleteUserFunction({ uid: userId });
        setUsersList(currentUsers => currentUsers.filter(user => user.id !== userId));
      } catch (err) {
        console.error("Error deleting user:", err);
        alert(`Failed to delete user: ${err.message}`);
      } finally {
        setDeletingUserId(null);
      }
    }
  };

  return (
    <>
      <EditUserModal
        user={editingUser}
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        onSave={handleSaveUser}
        isSaving={isSavingUser}
      />

      <div className="user-management-section">
        <h2 className="list-header">Add a New User</h2>
        <form onSubmit={handleSubmit} className="user-form">
          {error && <p className="form-error-message">{error}</p>}
          {successMessage && <p className="form-success-message">{successMessage}</p>}
          <div className="form-grid">
            <div className="form-field">
              <label htmlFor="firstName">First Name</label>
              <input id="firstName" type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="lastName">Last Name</label>
              <input id="lastName" type="text" value={lastName} onChange={e => setLastName(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="email">Email Address</label>
              <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="password">Password</label>
              <input id="password" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
            </div>
            <div className="form-field">
              <label htmlFor="role">Role</label>
              <select id="role" value={role} onChange={e => setRole(e.target.value)}>
                <option value="contributor">Contributor</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="form-field-action">
              <button type="submit" className="btn-submit-user" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="user-management-section">
        <h2 className="list-header">Current Users</h2>
        {listLoading ? (
          <p>Loading users...</p>
        ) : (
          <ul className="user-list">
            {usersList.map(user => (
              <li key={user.id} className="user-list-item">
                <div className="user-info">
                  <strong>{user.firstName} {user.lastName}</strong> ({user.email})
                </div>
                <div className="user-role">
                  <span className={`role-badge role-${user.role}`}>{user.role}</span>
                </div>
                <div className="user-actions">
                  <button className="btn btn-edit" onClick={() => handleEditUser(user.id)}>Edit</button>
                  <button 
                    className="btn btn-delete" 
                    onClick={() => handleDeleteUser(user.id)}
                    disabled={deletingUserId === user.id}
                  >
                    {deletingUserId === user.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
}


// --- UPDATED: The StatusIndicator component now uses the new, specific CSS classes ---
const StatusIndicator = ({ hasCoordinates }) => {
  const statusClass = hasCoordinates ? 'gps-status-valid' : 'gps-status-missing';
  const title = hasCoordinates ? 'GPS Coordinates are present' : 'GPS Coordinates are missing';
  return <div className={`gps-status-indicator ${statusClass}`} title={title}></div>;
};

function DashboardPage() {
  const { currentUser } = useAuth();
  
  const [activeTab, setActiveTab] = useState('resources');

  const [myResources, setMyResources] = useState([]);
  const [pendingResources, setPendingResources] = useState([]);
  const [otherApprovedResources, setOtherApprovedResources] = useState([]);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [resourceToDelete, setResourceToDelete] = useState(null);

  useEffect(() => {
    if (!currentUser) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const resourcesRef = collection(db, "resources");
    const unsubscribes = [];

    if (currentUser.role === 'admin') {
      const pendingQuery = query(resourcesRef, where("status", "==", "pending"));
      unsubscribes.push(onSnapshot(pendingQuery, (snapshot) => {
        setPendingResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }));

      const myResourcesQuery = query(resourcesRef, where("createdBy", "==", currentUser.uid));
      unsubscribes.push(onSnapshot(myResourcesQuery, (snapshot) => {
        setMyResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      }));

      const otherResourcesQuery = query(
        resourcesRef,
        where("status", "==", "approved"),
        where("createdBy", "!=", currentUser.uid)
      );
      unsubscribes.push(onSnapshot(otherResourcesQuery, (snapshot) => {
        setOtherApprovedResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      }, (err) => {
        console.error("Error fetching other resources: ", err);
        setError("An error occurred fetching resources. You may need to create a Firestore index. See the console for the link.");
        setLoading(false);
      }));

    } else {
      const q = query(resourcesRef, where("createdBy", "==", currentUser.uid));
      unsubscribes.push(onSnapshot(q, (snapshot) => {
        setMyResources(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      }));
    }

    return () => unsubscribes.forEach(unsub => unsub());
  }, [currentUser]);

  const handleApprove = async (resourceId) => {
    const resourceDocRef = doc(db, "resources", resourceId);
    try {
      await updateDoc(resourceDocRef, { status: "approved" });
    } catch (err) {
      console.error("Error approving resource: ", err);
      alert("There was an error approving the resource.");
    }
  };

  const handleDeleteClick = (resource) => {
    setResourceToDelete(resource);
    setShowConfirmModal(true);
  };

  const cancelDelete = () => {
    setShowConfirmModal(false);
    setResourceToDelete(null);
  };

  const confirmDelete = async () => {
    if (resourceToDelete) {
      try {
        await deleteDoc(doc(db, "resources", resourceToDelete.id));
      } catch (err) {
        console.error("Error removing document: ", err);
        alert("There was an error deleting the resource.");
      } finally {
        cancelDelete();
      }
    }
  };

  const ResourceList = ({ resources }) => (
    <ul className="resource-list">
      {resources.map(resource => (
        <li key={resource.id} className="resource-item">
          <StatusIndicator hasCoordinates={!!resource.coordinates} />
          <div className="resource-info">
            <h3>{resource.organizationName}</h3>
          </div>
          <div className="resource-actions">
            <Link to={`/edit-resource/${resource.id}`} className="btn btn-edit">Edit</Link>
            <button className="btn btn-delete" onClick={() => handleDeleteClick(resource)}>Delete</button>
          </div>
        </li>
      ))}
    </ul>
  );
  
  return (
    <>
      {showConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Confirm Deletion</h2>
            <p>Are you sure you want to delete the resource: <strong>{resourceToDelete?.organizationName}</strong>?</p>
            <p className="modal-warning">This action cannot be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-cancel" onClick={cancelDelete}>Cancel</button>
              <button className="btn btn-confirm-delete" onClick={confirmDelete}>Yes, Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-container">
        <div className="dashboard-content">
          <div className="dashboard-header-container">
            <div className="dashboard-title-group">
              <h1 className="dashboard-header">My Dashboard</h1>
              {currentUser && <p className="dashboard-greeting">Welcome back, {currentUser.firstName || 'User'}!</p>}
            </div>
            {currentUser?.role === 'admin' && (
              <div className="dashboard-tabs">
                <button
                  className={`tab-button ${activeTab === 'resources' ? 'active' : ''}`}
                  onClick={() => setActiveTab('resources')}
                >
                  Resources
                </button>
                <button
                  className={`tab-button ${activeTab === 'categoryManagement' ? 'active' : ''}`}
                  onClick={() => setActiveTab('categoryManagement')}
                >
                  Categories
                </button>
                <button
                  className={`tab-button ${activeTab === 'userManagement' ? 'active' : ''}`}
                  onClick={() => setActiveTab('userManagement')}
                >
                  User Management
                </button>
              </div>
            )}
          </div>
          
          {loading && <p className="loading-message">Loading...</p>}
          {error && <p className="no-resources-message">{error}</p>}
          
          {activeTab === 'resources' && !loading && (
            <>
              {currentUser?.role === 'admin' && (
                <>
                  <div className="resources-section">
                    <h2 className="list-header">Pending Approval ({pendingResources.length})</h2>
                    {pendingResources.length > 0 ? (
                      <ul className="resource-list">
                        {pendingResources.map(resource => (
                          <li key={resource.id} className="resource-item">
                            <StatusIndicator hasCoordinates={!!resource.coordinates} />
                            <div className="resource-info"><h3>{resource.organizationName}</h3></div>
                            <div className="resource-actions">
                              <button className="btn btn-approve" onClick={() => handleApprove(resource.id)}>Approve</button>
                              <Link to={`/edit-resource/${resource.id}`} className="btn btn-edit">Edit</Link>
                              <button className="btn btn-delete" onClick={() => handleDeleteClick(resource)}>Delete</button>
                            </div>
                          </li>
                        ))}
                      </ul>
                    ) : <p className="no-resources-message">No resources are currently pending approval.</p>}
                  </div>
                  
                  <div className="resources-section">
                    <h2 className="list-header">My Resources ({myResources.length})</h2>
                    {myResources.length > 0 ? <ResourceList resources={myResources} /> : <p className="no-resources-message">You haven't added any resources yet.</p>}
                  </div>

                  <div className="resources-section">
                    <h2 className="list-header">Other Resources ({otherApprovedResources.length})</h2>
                    {otherApprovedResources.length > 0 ? <ResourceList resources={otherApprovedResources} /> : <p className="no-resources-message">No other resources have been approved yet.</p>}
                  </div>
                </>
              )}

              {currentUser?.role === 'contributor' && (
                <div className="resources-section">
                  <h2 className="list-header">My Submitted Resources</h2>
                  {myResources.length > 0 ? (
                    <ul className="resource-list">
                      {myResources.map(resource => (
                        <li key={resource.id} className="resource-item">
                          <StatusIndicator hasCoordinates={!!resource.coordinates} />
                          <div className="resource-info">
                            <h3>{resource.organizationName}</h3>
                            <p>Status: <span className={`status-${resource.status}`}>{resource.status}</span></p>
                          </div>
                          <div className="resource-actions">
                            <Link to={`/edit-resource/${resource.id}`} className="btn btn-edit">Edit</Link>
                            <button className="btn btn-delete" onClick={() => handleDeleteClick(resource)}>Delete</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : <p className="no-resources-message">You haven't added any resources yet.</p>}
                </div>
              )}
            </>
          )}

          {activeTab === 'categoryManagement' && currentUser?.role === 'admin' && !loading && (
            <ManageCategoriesPage />
          )}

          {activeTab === 'userManagement' && currentUser?.role === 'admin' && !loading && (
            <UserManagementPanel />
          )}
        </div>
      </div>
    </>
  );
}

export default DashboardPage;